import { useState, useEffect } from "react";
import { MessageCircle, Send, Heart, MoreHorizontal, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MentionTextArea } from "./MentionTextArea";
import { MentionText } from "./MentionText";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAnonymousUser } from "@/hooks/useAnonymousUser";

interface Comment {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  profile?: {
    username: string;
    farm_name: string;
    avatar_url: string;
  };
}

interface CommentsSectionProps {
  postId: string;
  onCommentsCountChange?: (count: number) => void;
  isExpanded?: boolean;
  onToggleComments?: () => void;
}

export const CommentsSection = ({ postId, onCommentsCountChange, isExpanded = false, onToggleComments }: CommentsSectionProps) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const { anonymousUser } = useAnonymousUser();

  const fetchComments = async () => {
    try {
      console.log('Fetching comments for post:', postId);
      
      // First get comments
      const { data: commentsData, error: commentsError } = await supabase
        .from('comments')
        .select('*')
        .eq('post_id', postId)
        .order('created_at', { ascending: false });

      if (commentsError) {
        console.error('Error fetching comments:', commentsError);
        setLoading(false);
        return;
      }

      console.log('Comments data:', commentsData);

      // If we have comments, get the profile data for each comment
      if (commentsData && commentsData.length > 0) {
        const userIds = commentsData.map(comment => comment.user_id);
        
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, username, farm_name, avatar_url')
          .in('user_id', userIds);

        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
        }

        console.log('Profiles data:', profilesData);

        // Combine comments with profile data
        const commentsWithProfiles = commentsData.map(comment => ({
          ...comment,
          profile: profilesData?.find(profile => profile.user_id === comment.user_id)
        }));

        setComments(commentsWithProfiles);
        onCommentsCountChange?.(commentsWithProfiles.length);
      } else {
        setComments([]);
        onCommentsCountChange?.(0);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [postId]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        // Authenticated user flow - direct database interaction
        console.log('Submitting comment:', { postId, userId: user.id, content: newComment.trim() });

        const { error } = await supabase
          .from('comments')
          .insert({
            post_id: postId,
            user_id: user.id,
            content: newComment.trim()
          });

        if (error) {
          console.error('Error posting comment:', error);
          toast({
            title: "Error",
            description: "Failed to post comment. Please try again.",
            variant: "destructive",
          });
          return;
        }
      } else {
        // Anonymous user flow - use Edge Function
        if (!anonymousUser) {
          toast({
            title: "Unable to post comment",
            description: "Please refresh the page and try again.",
            variant: "destructive",
          });
          return;
        }

        console.log('Submitting anonymous comment:', { postId, anonymousUserId: anonymousUser.id, content: newComment.trim() });

        const { error } = await supabase.functions.invoke('anonymous-interactions', {
          body: {
            action: 'add_comment',
            anonymousUserId: anonymousUser.id,
            sessionId: anonymousUser.sessionId,
            postId,
            content: newComment.trim()
          }
        });

        if (error) {
          console.error('Error posting anonymous comment:', error);
          toast({
            title: "Error",
            description: "Failed to post comment. Please try again.",
            variant: "destructive",
          });
          return;
        }
      }

      setNewComment("");
      await fetchComments(); // Refresh comments
      toast({
        title: "Comment posted!",
        description: "Your comment has been added successfully.",
      });
    } catch (error) {
      console.error('Error posting comment:', error);
      toast({
        title: "Error",
        description: "Failed to post comment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      const { error } = await supabase
        .from('comments')
        .delete()
        .eq('id', commentId);

      if (error) {
        console.error('Error deleting comment:', error);
        toast({
          title: "Error",
          description: "Failed to delete comment.",
          variant: "destructive",
        });
        return;
      }

      await fetchComments(); // Refresh comments
      toast({
        title: "Comment deleted",
        description: "Your comment has been removed.",
      });
    } catch (error) {
      console.error('Error deleting comment:', error);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return '1d ago';
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  const handleMentionClick = (username: string) => {
    console.log('Navigate to user profile:', username);
    // TODO: Navigate to user profile
  };

  if (!isExpanded) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Comments Section */}
      <div className="space-y-4 bg-gray-50 rounded-lg p-4">
        {/* Comment Form */}
        <form onSubmit={handleSubmitComment} className="space-y-3">
          <MentionTextArea
            value={newComment}
            onChange={setNewComment}
            placeholder="Share your thoughts or advice... Use @username to mention someone!"
            className="min-h-[80px] resize-none"
            disabled={submitting}
          />
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-500">
              💡 Tip: Type @ to mention other farmers
            </div>
            <Button
              type="submit"
              disabled={!newComment.trim() || submitting}
              size="sm"
              className="bg-green-600 hover:bg-green-700"
            >
              <Send size={14} className="mr-1" />
              {submitting ? "Posting..." : "Post Comment"}
            </Button>
          </div>
        </form>

        {/* Comments List */}
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-4 text-gray-500">Loading comments...</div>
          ) : comments.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              <MessageCircle className="mx-auto mb-2 text-gray-400" size={24} />
              <p className="text-sm">No comments yet</p>
              <p className="text-xs">Be the first to share your thoughts!</p>
            </div>
          ) : (
            comments.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                onDelete={handleDeleteComment}
                formatTimestamp={formatTimestamp}
                onMentionClick={handleMentionClick}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

interface CommentItemProps {
  comment: Comment;
  onDelete: (commentId: string) => void;
  formatTimestamp: (timestamp: string) => string;
  onMentionClick: (username: string) => void;
}

const CommentItem = ({ comment, onDelete, formatTimestamp, onMentionClick }: CommentItemProps) => {
  const [isOwnComment, setIsOwnComment] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    const checkOwnership = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsOwnComment(user?.id === comment.user_id);
    };
    checkOwnership();
  }, [comment.user_id]);

  return (
    <div className="bg-white rounded-lg p-3 border border-gray-200">
      {/* Comment Header */}
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center space-x-2">
          <img
            src={comment.profile?.avatar_url || "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face"}
            alt={comment.profile?.farm_name || 'User'}
            className="w-6 h-6 rounded-full object-cover"
          />
          <div className="flex items-center space-x-2">
            <span className="font-medium text-sm text-gray-900">
              {comment.profile?.farm_name || 'Anonymous'}
            </span>
            <span className="text-gray-400 text-xs">•</span>
            <span className="text-gray-400 text-xs">
              {formatTimestamp(comment.created_at)}
            </span>
          </div>
        </div>

        {/* Menu Button */}
        {isOwnComment && (
          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              className="p-1 h-auto"
              onClick={() => setShowMenu(!showMenu)}
            >
              <MoreHorizontal size={14} className="text-gray-400" />
            </Button>
            
            {showMenu && (
              <div className="absolute right-0 top-6 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[100px]">
                <button
                  onClick={() => {
                    onDelete(comment.id);
                    setShowMenu(false);
                  }}
                  className="flex items-center space-x-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 w-full text-left rounded-lg"
                >
                  <Trash2 size={12} />
                  <span>Delete</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Comment Content with Mentions */}
      <MentionText 
        onMentionClick={onMentionClick}
        className="text-sm text-gray-800 leading-relaxed"
      >
        {comment.content}
      </MentionText>
    </div>
  );
};
